import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { Text, TextProps } from '@/components/TextComponent';
import { Column, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { withOpacity } from '@/styles/styles';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useGameResultModal } from './GameResultModal/GameResultModalProvider';

export type GameResult = {
    id: string;
    external_id: string;
    description: string;
    stake: number;
    potential_payout: number;
    score: number;
    status: 'PENDING' | 'DRAW' | 'WON' | 'LOST' | 'REFUNDED';
    amount_won: number;
    created_at: number;
    settled_at: number | null;
    avatar_url: string;
    opponents: Array<{
        username: string;
        score: number;
        avatar_url: string;
    }>;
    isBetrBucks: boolean;
    // Metadata fields added from arcade
    name?: string | null;
    image?: string;
};

type GameResultItemProps = {
    gameResult: GameResult;
};

type CompletedGameResultStyle = {
    backgroundColor: string;
    borderColor: string;
    gameNameColor: TextProps['color'];
    textColor: TextProps['color'];
    opponentNameColor: TextProps['color'];
    headerText: string;
    amount: number;
};

const useCompletedGameResultStyles = (gameResult: GameResult): CompletedGameResultStyle => {
    const { t } = useTranslation();
    // Determine result type based on status
    let resultType: 'won' | 'lost' | 'refunded' = 'refunded';
    switch (gameResult.status) {
        case 'REFUNDED':
        case 'DRAW':
            resultType = 'refunded';
            break;
        case 'WON':
            resultType = 'won';
            break;
        case 'LOST':
            resultType = 'lost';
            break;
    }

    const resultStyles: Record<'won' | 'lost' | 'refunded', CompletedGameResultStyle> = {
        won: {
            backgroundColor: withOpacity(ds.colorValue.background.success.strong, 0.12),
            // eslint-disable-next-line no-restricted-syntax
            borderColor: withOpacity(ds.colorValue.background.success.strong, 0.06), // todo: update when updating the utility colors
            gameNameColor: ds.colorToken.text.success,
            textColor: ds.colorToken.text.success,
            opponentNameColor: ds.colorToken.text.success,
            headerText: t('won'),
            amount: gameResult.amount_won,
        },
        refunded: {
            backgroundColor: ds.colorValue.background.neutral.subtler,
            borderColor: ds.colorValue.border.subtle,
            gameNameColor: ds.colorToken.text.primary,
            textColor: ds.colorToken.text.primary,
            opponentNameColor: 'neutral300',
            headerText: t('refunded'),
            amount: gameResult.stake,
        },
        lost: {
            backgroundColor: ds.colorValue.background.neutral.subtler,
            borderColor: ds.colorValue.border.subtle,
            gameNameColor: ds.colorToken.text.primary,
            textColor: 'neutral300',
            opponentNameColor: 'neutral300',
            headerText: t('lost'),
            amount: gameResult.stake,
        },
    };

    return resultStyles[resultType];
};

const PendingGameResultItem = ({ gameResult }: GameResultItemProps) => {
    const { showGameResultModal } = useGameResultModal();

    const handlePress = () => {
        showGameResultModal(gameResult);
    };

    return (
        <TouchableOpacity onPress={handlePress}>
            <Row {...containerProps}>
                <Column>
                    <Image style={styles.image} source={{ uri: gameResult.image }} />
                </Column>
                <Column flex={1}>
                    <Text variant="titleMedium2" color={ds.colorToken.text.primary}>
                        {gameResult.name}
                    </Text>
                    <Text variant="bodySmall2" color="neutral300">
                        To win {toLocaleCurrency(gameResult.potential_payout)}
                    </Text>
                </Column>
            </Row>
        </TouchableOpacity>
    );
};

const CompletedGameResultItem = ({ gameResult }: GameResultItemProps) => {
    const resultStyles = useCompletedGameResultStyles(gameResult);
    const { showGameResultModal } = useGameResultModal();

    const handlePress = () => {
        showGameResultModal(gameResult);
    };

    return (
        <TouchableOpacity onPress={handlePress}>
            <Row
                {...containerProps}
                style={{ borderColor: resultStyles.borderColor, backgroundColor: resultStyles.backgroundColor }}
            >
                <Column>
                    <Image style={styles.image} source={{ uri: gameResult.image }} />
                </Column>
                <Column flex={1}>
                    <Text variant="titleMedium2" color={resultStyles.gameNameColor}>
                        {gameResult.name}
                    </Text>
                    <Text variant="bodySmall2" color={resultStyles.opponentNameColor} numberOfLines={1}>
                        vs {gameResult.opponents[0]?.username || 'Unknown Opponent'}
                    </Text>
                </Column>
                <Column>
                    <Text variant="labelLarge2" color={resultStyles.textColor} textAlign="right">
                        {resultStyles.headerText}
                    </Text>
                    <Text variant="titleMedium2" color={resultStyles.textColor} textAlign="right">
                        {toLocaleCurrency(resultStyles.amount)}
                    </Text>
                </Column>
            </Row>
        </TouchableOpacity>
    );
};

export const GameResultItem = ({ gameResult }: GameResultItemProps) => {
    switch (gameResult.status) {
        case 'PENDING':
            return <PendingGameResultItem gameResult={gameResult} />;
        default:
            return <CompletedGameResultItem gameResult={gameResult} />;
    }
};

const containerProps = {
    height: 80,
    p: 's16',
    gap: 's16',
    backgroundColor: ds.colorToken.background.neutral.subtler,
    borderWidth: 1,
    style: { borderColor: ds.colorValue.border.subtle },
    borderRadius: 'r16',
} as const;

const styles = StyleSheet.create({
    image: {
        width: 48,
        height: 48,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
});
